import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export interface LayoutState {
    generalSpinner: boolean;

    confirmationModal: ConfirmModalState;

    docDownloadUrl: string;
    dashboardStatuses: DashboardStatus[];
    jobStatuses: DashboardStatus[];

    currentUser: CurrentUser | null;
    logoUrl: string | null;

    navigation: NavigationItem[];
    sidebarMenu: SidebarMenuItem[];

    isErrorModalOpen: boolean;
    errorModalMessage: string | null;
    errorModalDetailMessage: string | null;

    featuresConfiguration: FeaturesConfiguration;
}

export interface FeaturesConfiguration {
    allowChangePassword: boolean;
    somEnabled: boolean;
    useSpotDefault: boolean;
    billingEnabled: boolean;
    otiEnabled: boolean;
    allowUserEdit: boolean;
    crossAccountMessagingEnabled: boolean;
    directoryDomain: string;
    isMasterAccount: boolean;
    renewbixEnabled: boolean;
    snapEnabled: boolean;
    staticReportsNewEnabled: boolean;
    unknownWarningsEnabled: boolean;
};

export interface BaseDashboardStatus {
    processUid: string;
    processStatus: number;
    eventTime: string;
    initiatorName: string;
    notificationType: (typeof layoutNotificationTypes)[keyof typeof layoutNotificationTypes];
    projectId?: number | string;
}

export interface DashboardLoadingStatus extends BaseDashboardStatus {
    notificationType: typeof layoutNotificationTypes.dashboardLoading;
    dashboard: string;
    progress: number | string;
    status: string;
}

export interface RunningJobPayload {
    progress: number | string;
    projectId: number | string;
    projectName: string;
    runBy: string;
    runMode: string;
    runStatus: string;
    taskId: number;
    taskName: string;
    scenarioId?: number | null;
    scenarioName?: string | null;
    segmentIdx?: number | null;
    startWork?: string | null;
    endWork?: string | null;
    statusMessage?: string | null;
}

export interface RunningJobStatus extends BaseDashboardStatus {
    notificationType: typeof layoutNotificationTypes.runningJobs;
    payload: RunningJobPayload;
}

export interface RunningPSOJobStatus extends BaseDashboardStatus {
    notificationType: typeof layoutNotificationTypes.runningPSOJobs;
    payload: RunningJobPayload;
}

export interface CopyFilesStatus extends BaseDashboardStatus {
    notificationType: typeof layoutNotificationTypes.copyFiles;
    progress: number | string;
    requestName: string;
}

export interface RunningFunctionsStatus extends BaseDashboardStatus {
    notificationType: typeof layoutNotificationTypes.runningFunctions;
    functionName: string;
    names: string[];
    status: string;
    type: number;
}

export interface RunningReportsStatus extends BaseDashboardStatus {
    notificationType: typeof layoutNotificationTypes.runningReports;
    athenaDbName: string;
    projectId: number | string;
    projectName: string;
    scenarioNames: string[];
    type: number;
}

export interface CopyTaskStatus extends BaseDashboardStatus {
    notificationType: typeof layoutNotificationTypes.copyTask;
    progress: number | string;
    statusMessage: string;
    taskId: number;
}

export interface RunningStaticReportStatus extends BaseDashboardStatus {
    notificationType: typeof layoutNotificationTypes.runningStaticReportQueries | typeof layoutNotificationTypes.runningPSOLogs;
    progress: number | string;
    requestName: string;
    statusMessage: string;
}

export type DashboardStatus =
    | DashboardLoadingStatus
    | RunningJobStatus
    | RunningPSOJobStatus
    | CopyFilesStatus
    | RunningFunctionsStatus
    | RunningReportsStatus
    | CopyTaskStatus
    | RunningStaticReportStatus;

export interface NavigationItem {
    text: string;
    url: string;
    active?: boolean;
};

export interface SidebarMenuItem {
    idx: number;
    link: string;
    icon: IconDefinition | JSX.Element;
    label: string;
    onClick?: () => void;
};

export interface CurrentUser {
    email: string;
    isSuperAdmin: boolean;
    userId: string;
    userName: string;
    userRoles: string[];
};

export type ConfirmModalState = {
    isOpen: boolean;
    message: string;
    title?: string;
};

export const uiPages = {
    scenarioGroup: 1,
    scenario: 2
};

export const scenarioReportQueueType = {
    load: 0,
    delete: 1
};

export const layoutNotificationTypes = {
    dashboardLoading: 1,
    runningJobs: 2,
    copyFiles: 3,
    runningPSOJobs: 4,
    runningFunctions: 5,
    runningReports: 6,
    copyTask: 7,
    runningStaticReportQueries: 8,
    runningPSOLogs: 9,
} as const;
